import { formatDate, MappingStatus, MappingStyles } from "../../../utils/utils";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import JSZip from "jszip";

const Details = ({ application, distributionStatus }) => {
  // ✅ Helper to calculate difference in days
  const getProcessingDays = (createdAt, updatedAt) => {
    if (!createdAt || !updatedAt) return "N/A";
    const created = new Date(createdAt);
    const approved = new Date(updatedAt);
    const diffTime = approved - created;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // convert ms to days
  };

  const loanProcessingDays = getProcessingDays(
    application?.createdAt,
    application?.updatedAt // <-- make sure backend sends this field
  );
  const handleDownloadProfile = async () => {
    try {
      const mainZip = new JSZip();

      // Details sheet data
      const detailsData = [
        {
          "Applicant Name": application?.user.name || "",
          "Application ID": application?._id || "",
          "Applied On": formatDate(application?.createdAt) || "",
          "Assigned To": application?.assignedTo?.name || "",
          Status: application?.status || "",
          "Loan Amount": application?.principle || "",
          "Loan Type": application?.loanType || ""
        },
      ];
      const detailsWorkbook = XLSX.utils.book_new();
      const detailsSheet = XLSX.utils.json_to_sheet(detailsData);
      XLSX.utils.book_append_sheet(detailsWorkbook, detailsSheet, "Details");
      const detailsExcelBuffer = XLSX.write(detailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("Details.xlsx", detailsExcelBuffer);

      // PersonalDetails sheet data
      const personalDetailsData = [
        {
          "Full Name": application.user.name || "",
          "Phone Number": application.user.phoneNumber || "",
          "Email ID": application.user.email || "",
          Street: application.user.address?.street || "",
          City: application.user.address?.city || "",
          State: application.user.address?.state || "",
          Pincode: application.user.address?.pincode || "",
        },
      ];
      const personalDetailsWorkbook = XLSX.utils.book_new();
      const personalSheet = XLSX.utils.json_to_sheet(personalDetailsData);
      XLSX.utils.book_append_sheet(
        personalDetailsWorkbook,
        personalSheet,
        "PersonalDetails"
      );
      const personalDetailsExcelBuffer = XLSX.write(personalDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("PersonalDetails.xlsx", personalDetailsExcelBuffer);

      // FinancialDetails sheet data
      const financialDetailsData = [
        {
          "Credit Score": 750,
          "Credit Limit": "₹3,00,000",
          "Monthly Income": "₹80,000",
          "Business Name": "IoT Business",
          "Business Type": "Private",
          "Years in Business": "40 years",
          "Existing EMI's": "1 Active Loan (₹60,000)",
        },
      ];
      const financialDetailsWorkbook = XLSX.utils.book_new();
      const financialSheet = XLSX.utils.json_to_sheet(financialDetailsData);
      XLSX.utils.book_append_sheet(
        financialDetailsWorkbook,
        financialSheet,
        "FinancialDetails"
      );
      const financialDetailsExcelBuffer = XLSX.write(financialDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("FinancialDetails.xlsx", financialDetailsExcelBuffer);

      // LoanDetails sheet data
      const loanDetailsData = [
        {
          "Requested Amount": application.principle || "",
          "Monthly EMI": application.emi || "",
          "Interest Rate": application.interest || "",
          "Tenure Requested": application.monthsToRepay || "",
          "Loan Type": application?.loanType || "",
          "Processing Fee": application.processingFee || "",
          "Insurance Fees": application.insuranceFee || "",
          "Applied Insurance": application.appliedInsurance || "",
          "Platform Fees": application.platformFee || "",
        },
      ];
      const loanDetailsWorkbook = XLSX.utils.book_new();
      const loanSheet = XLSX.utils.json_to_sheet(loanDetailsData);
      XLSX.utils.book_append_sheet(
        loanDetailsWorkbook,
        loanSheet,
        "LoanDetails"
      );
      const loanDetailsExcelBuffer = XLSX.write(loanDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("LoanDetails.xlsx", loanDetailsExcelBuffer);

      // SubmittedDocuments Info sheet data (metadata)
      const submittedDocsData = (application.documents || []).map((doc) => ({
        "Document Type": doc.documentType || "",
        "Document Number": doc.documentNumber || "",
        "Upload Date": formatDate(doc.uploadDate) || "",
        Status: doc.status || "",
      }));
      const submittedDocsInfoWorkbook = XLSX.utils.book_new();
      const submittedDocsSheet = XLSX.utils.json_to_sheet(submittedDocsData);
      XLSX.utils.book_append_sheet(
        submittedDocsInfoWorkbook,
        submittedDocsSheet,
        "SubmittedDocumentsInfo"
      );
      const submittedDocsInfoExcelBuffer = XLSX.write(
        submittedDocsInfoWorkbook,
        { bookType: "xlsx", type: "array" }
      );
      mainZip.file("SubmittedDocumentsInfo.xlsx", submittedDocsInfoExcelBuffer);

      // --- Create a separate zip for documents ---
      if (application.documents && application.documents.length > 0) {
        const documentsZip = new JSZip();
        let documentsAdded = 0;

        // Helper to fetch and add file to the documentsZip
        const addDocumentToZip = async (doc) => {
          // IMPORTANT ASSUMPTION:
          // Each 'doc' object has a 'url' property with a direct link to the document file.
          // And optionally a 'fileName' property for the original file name.
          // Adjust this part if your document data structure is different.
          if (!doc.url) {
            console.warn("Document skipped: URL is missing.", doc);
            return;
          }

          try {
            const response = await fetch(doc.url);
            if (!response.ok) {
              console.error(
                `Failed to fetch document ${doc.fileName || doc.url}: ${
                  response.statusText
                }`
              );
              return; // Skip this document
            }
            const fileBlob = await response.blob();

            // Determine filename
            let fileName = doc.fileName;
            if (!fileName) {
              const urlPath = new URL(doc.url).pathname;
              fileName =
                urlPath.substring(urlPath.lastIndexOf("/") + 1) ||
                `${doc.documentType?.replace(/\s+/g, "_") || "document"}_${
                  doc.documentNumber || Date.now()
                }`;
              // Ensure a fallback extension if not in parsed filename
              if (!fileName.includes(".")) {
                const extensionMatch = doc.url.match(
                  /\.([0-9a-z]+)(?:[\?#]|$)/i
                );
                fileName += extensionMatch ? `.${extensionMatch[1]}` : ".file";
              }
            }
            documentsZip.file(fileName, fileBlob);
            documentsAdded++;
          } catch (docError) {
            console.error(
              `Error processing document ${doc.fileName || doc.url}:`,
              docError
            );
          }
        };
        await Promise.all(
          (application.documents || []).map((doc) => addDocumentToZip(doc))
        );
        if (documentsAdded > 0) {
          const documentsZipBlob = await documentsZip.generateAsync({
            type: "blob",
          });
          mainZip.file("Documents.zip", documentsZipBlob);
        }
      }

      // Generate zip and trigger download
      const content = await mainZip.generateAsync({ type: "blob" });
      const blobUrl = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${application.user.name.replace(/\s+/g, "_")}_profile.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading user profile:", error);
      alert("Failed to download user profile. Please try again.");
    }
  };

  return (
    <div className="bg-[#131313] rounded-lg p-4 mb-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-white text-xl font-bold">
            {application?.user?.name || "N/A"}
          </h2>
          <p className="text-gray-400 text-sm">#{application?._id}</p>
          <p className="text-gray-400 text-sm">
            Applied on {formatDate(application?.createdAt)} {formatDate(application?.updatedAt)}
          </p>

          <div className="flex flex-col sm:flex-row mt-4 gap-4 sm:gap-0">
            <div className="mr-20">
              <p className="text-gray-400 text-sm">Assigned To</p>
              <p className="text-white">{application?.assignedTo?.name || "N/A"}</p>
              {/* <p className="text-[#FFCA20] text-sm">Send a note</p> */}
            </div>
            <div>
              <p className="text-gray-400 text-sm">Status</p>

              <span
                className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${MappingStyles(
                  distributionStatus ? distributionStatus : application?.status
                )}`}
              >
                {MappingStatus(distributionStatus ? distributionStatus : application?.status)}
              </span>
            </div>
             <div className="ml-4 text-center">
              <p className="text-gray-400 text-sm">Loan Processing(No of Days)</p>
              <span className="text-white font-semibold">
                {loanProcessingDays}
              </span>

            </div>
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end">
          <p className="text-white text-2xl font-bold">
            ₹ {application?.principle}
          </p>
          <p className="text-gray-400 text-sm text-right">{application?.loanType}</p>
          <button
            onClick={handleDownloadProfile}
            className="bg-[#FFCA20] text-black px-4 py-2 rounded-md mt-6 flex items-center"
          >
            Download User Profile <Download className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Details;
